import { useEffect, useRef, useState } from 'react';
import { api, formatApiErrorDetail } from '@/lib/api';
import { PDF } from '@/constants/testIds';
import { UploadCloud, CheckCircle2, XCircle, Loader2, FileText, Mail } from 'lucide-react';

export default function PdfDropzone({ onUploaded }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [imapStatus, setImapStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchStatus = async () => {
      try {
        const { data } = await api.get('/inbound/imap-status');
        if (alive) setImapStatus(data);
      } catch {
        // Non-admin role gets 403 — silently hide the badge.
        if (alive) setImapStatus(null);
      }
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const pdfs = Array.from(files).filter((f) => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfs.length === 0) return;
    setBusy(true);
    const newResults = [];
    for (const f of pdfs) {
      try {
        const form = new FormData();
        form.append('file', f);
        const { data } = await api.post('/pdf/upload', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        newResults.push(data);
      } catch (e) {
        newResults.push({ filename: f.name, ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      }
    }
    setResults((prev) => [...newResults, ...prev].slice(0, 8));
    setBusy(false);
    if (onUploaded) onUploaded(newResults);
  };

  return (
    <div className="bg-white border border-[#E5E6E3] rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#6A6D66]">Tuonti</p>
          <h3 className="font-heading text-lg text-[#1C1C1A]">Ostotilaus-PDF:t</h3>
        </div>
        {busy && (
          <span className="flex items-center gap-1.5 text-sm text-[#2A4B30]">
            <Loader2 size={14} className="animate-spin" />
            Luetaan…
          </span>
        )}
      </div>

      {imapStatus && imapStatus.enabled && (
        <div
          data-testid="pdf-imap-status"
          className={`mb-3 rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${
            imapStatus.last_error
              ? 'bg-[#FBEAE5] border-[#C05A3C]/30 text-[#C05A3C]'
              : 'bg-[#E8F0E9] border-[#3B6E44]/30 text-[#2A4B30]'
          }`}
        >
          <Mail size={14} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">
              {imapStatus.last_error ? 'Sähköpostin haku epäonnistui' : 'Sähköpostien autotuonti päällä'}
            </div>
            <div className="opacity-80 truncate">
              {imapStatus.username} · joka {imapStatus.interval_seconds}s
              {imapStatus.last_run && !imapStatus.last_error && (
                <> · viim. tark. {new Date(imapStatus.last_run).toLocaleTimeString('fi-FI')}</>
              )}
            </div>
            {imapStatus.last_error && <div className="opacity-90 mt-0.5 truncate">{imapStatus.last_error}</div>}
          </div>
        </div>
      )}

      <div
        data-testid={PDF.dropzone}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed transition-colors p-6 flex flex-col items-center justify-center text-center ${
          dragOver ? 'border-[#2A4B30] bg-[#E8F0E9]' : 'border-[#D6E0D4] bg-[#F9F9F6] hover:border-[#2A4B30] hover:bg-[#F4F6F3]'
        }`}
      >
        <UploadCloud size={28} className="text-[#2A4B30] mb-2" strokeWidth={1.8} />
        <p className="font-medium text-[#1C1C1A]">Raahaa PDF:t tähän</p>
        <p className="text-xs text-[#6A6D66] mt-1">tai klikkaa valitaksesi tiedostoja (Prisma / S-Market ostotilaukset)</p>
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          data-testid={PDF.fileInput}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>

      {results.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid={PDF.resultsList}>
          {results.map((r, i) => (
            <li
              key={i}
              className={`flex items-start gap-2.5 p-2.5 rounded-md text-sm border ${
                r.ok ? 'bg-[#E8F0E9] border-[#3B6E44]/30' : 'bg-[#FBEAE5] border-[#C05A3C]/30'
              }`}
            >
              {r.ok ? (
                <CheckCircle2 size={16} className="text-[#3B6E44] mt-0.5 shrink-0" />
              ) : (
                <XCircle size={16} className="text-[#C05A3C] mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <FileText size={12} className="text-[#6A6D66]" />
                  <span className="font-mono text-xs text-[#6A6D66] truncate">{r.filename}</span>
                </div>
                {r.ok ? (
                  <p className="text-[#1C1C1A] mt-0.5">
                    <span className="font-medium">{r.customer.ryhma} · {r.customer.ketju} · {r.customer.asiakas}</span>
                    <span className="text-[#6A6D66]"> — {r.date} · </span>
                    <span className="font-mono font-medium">+{r.added_kg} kg</span>
                    <span className="text-[#6A6D66]"> ({r.boxes} ltk)</span>
                  </p>
                ) : (
                  <p className="text-[#C05A3C] mt-0.5">{r.error}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
